import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { hatPlugin, istNativeApp } from './nativePlatform';
import { apkDownloadLink, normalisiereSha256, type AppRelease } from './appRelease';

/**
 * Update der Android-Huelle direkt in der App (docs/android-app-vertrag.md, E21;
 * Review R4).
 *
 * Anbindung an das Native-Plugin `ApkUpdate`
 * (android/app/src/main/java/de/gefasmbh/sigeko/ApkUpdatePlugin.java). ZWEI
 * Phasen, weil die Installation die App beendet:
 *
 *   1. `bereiteApkUpdateVor` → `installieren({ url, sha256 })`: laden
 *      (signierte URL aus der Release-Info der eigenen Variante), sha256 und
 *      APK pruefen → `bereit`. Die Datei bleibt im App-Cache liegen.
 *   2. `installiereApkUpdate` → `freigeben()`: erst NACHDEM die Web-Seite die
 *      Eingaben gesichert hat (appUpdate.ts) — PackageInstaller-Session,
 *      Bestaetigung bzw. still ab Android 12.
 *
 * Fortschritt kommt als Ereignis `status`. Registriert wird erst beim ersten
 * Zugriff (Muster appFlags.ts) — im Browser nie. Aeltere APKs kennen das
 * Plugin nicht: `apkUpdateVerfuegbar()` ist dann false, und der Aufrufer nimmt
 * den bisherigen Weg (System-Browser).
 *
 * Nichts hier wirft: jeder Fehler endet als Status `fehler` mit Klartext.
 */

export type ApkUpdatePhase =
    | 'laden'
    | 'pruefen'
    | 'bereit'
    | 'erlaubnis_noetig'
    | 'bestaetigen'
    | 'installieren'
    | 'fertig'
    | 'fehler';

export interface ApkUpdateStatus {
    phase: ApkUpdatePhase;
    /** Nur bei `laden` und bekannter Dateigroesse: 0–100. */
    prozent?: number | null;
    /** Nur bei `fehler`: Klartext fuer die Oberflaeche. */
    meldung?: string | null;
}

/** Womit Phase 1 (laden + pruefen) endet; `abgebrochen` = vom Aufrufer abgebrochen, nichts gemeldet. */
export type ApkVorbereitung = 'bereit' | 'erlaubnis_noetig' | 'fehler' | 'abgebrochen';
/** Womit Phase 2 (installieren) endet — beim stillen Update beendet Android die App vorher. */
export type ApkUpdateErgebnis = 'fertig' | 'bestaetigen' | 'erlaubnis_noetig' | 'fehler';

export interface ApkUpdatePlugin {
    /** Phase 1: laden und pruefen. Loest mit `bereit` oder `erlaubnis_noetig` auf, lehnt bei Fehlern ab. */
    installieren(opts: { url: string; sha256: string }): Promise<{ phase: 'bereit' | 'erlaubnis_noetig' }>;
    /** Phase 2: die vorbereitete Datei installieren. Ohne vorbereitete Datei → Ablehnung. */
    freigeben(): Promise<{ phase: 'bestaetigen' | 'fertig' | 'erlaubnis_noetig' }>;
    /** Laufenden Download abbrechen. */
    abbrechen(): Promise<void>;
    /** „Apps aus dieser Quelle zulassen" fuer diese App erteilt? */
    erlaubnis(): Promise<{ erlaubt: boolean }>;
    addListener(eventName: 'status', listener: (status: ApkUpdateStatus) => void): Promise<PluginListenerHandle>;
}

const PHASEN: readonly ApkUpdatePhase[] = ['laden', 'pruefen', 'bereit', 'erlaubnis_noetig', 'bestaetigen', 'installieren', 'fertig', 'fehler'];

let plugin: ApkUpdatePlugin | null = null;

/** Das Plugin — beim ersten Aufruf registriert, danach immer derselbe Proxy. */
export function apkUpdatePlugin(): ApkUpdatePlugin {
    if (!plugin) plugin = registerPlugin<ApkUpdatePlugin>('ApkUpdate');
    return plugin;
}

/** Kann diese App sich selbst aktualisieren? Nur in der App UND mit einer Huelle, die das Plugin kennt. */
export function apkUpdateVerfuegbar(): boolean {
    return istNativeApp() && hatPlugin('ApkUpdate');
}

/** Pfad der signierten Links des Eimers `app-releases` (Supabase Storage). */
const ERLAUBTER_PFAD = '/storage/v1/object/sign/app-releases/';

/**
 * Nur signierte Links des Release-Eimers bei Supabase (Review R4, P2-2) —
 * dieselbe Regel wie `ApkUpdatePlugin.pruefeUrl`: https, Host
 * `*.supabase.co`, Port 443, Pfad `/storage/v1/object/sign/app-releases/…`,
 * keine Anmeldedaten, keine Pfadtricks.
 */
export function istErlaubteApkUrl(url: string): boolean {
    let u: URL;
    try {
        u = new URL(url);
    } catch {
        return false;
    }
    if (u.protocol !== 'https:' || u.username || u.password) return false;
    const host = u.hostname.toLowerCase();
    if (!host.endsWith('.supabase.co') || host.length <= '.supabase.co'.length) return false;
    if (u.port !== '' && u.port !== '443') return false;
    // `URL` loest `..` schon auf — geprueft wird deshalb auch die Rohfassung.
    const roh = url.replace(/^[^:]+:\/\/[^/]+/, '').split(/[?#]/)[0] ?? '';
    const klein = roh.toLowerCase();
    if (roh.includes('..') || klein.includes('%2e') || klein.includes('%2f') || klein.includes('%5c') || roh.includes('\\')) return false;
    return u.pathname.startsWith(ERLAUBTER_PFAD) && u.pathname.length > ERLAUBTER_PFAD.length;
}

/** Rohes Ereignis der Bruecke → gepruefter Status, sonst null (unbekanntes wird verworfen). */
export function normalisiereApkStatus(roh: unknown): ApkUpdateStatus | null {
    if (!roh || typeof roh !== 'object') return null;
    const r = roh as Record<string, unknown>;
    const phase = r.phase;
    if (typeof phase !== 'string' || !(PHASEN as readonly string[]).includes(phase)) return null;
    const p = r.prozent;
    const prozent = typeof p === 'number' && Number.isFinite(p) ? Math.min(100, Math.max(0, Math.round(p))) : null;
    const meldung = typeof r.meldung === 'string' && r.meldung.trim() ? r.meldung.trim() : null;
    return { phase: phase as ApkUpdatePhase, prozent, meldung };
}

/** Fehlerobjekt der Bruecke (oder sonst etwas) → Klartext. */
export function apkFehlerText(e: unknown): string {
    const text = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
    return text.trim() || 'Update fehlgeschlagen.';
}

/**
 * Ein nach `bestaetigen` weiter lauschender Zuhoerer: der Android-Dialog
 * meldet sich erst spaeter (Abbruch → `fehler`). Er endet beim naechsten
 * Endereignis oder spaetestens mit dem naechsten Aufruf.
 */
let nachlaufenderZuhoerer: (() => void) | null = null;

const nachlaufendenEntfernen = (): void => {
    const ende = nachlaufenderZuhoerer;
    nachlaufenderZuhoerer = null;
    if (ende) ende();
};

const melder = (onStatus: (status: ApkUpdateStatus) => void) => (s: ApkUpdateStatus): void => {
    try {
        onStatus(s);
    } catch (e) {
        console.warn('[apkUpdate] Statusanzeige warf:', e);
    }
};

/**
 * Einen Plugin-Aufruf mit Status-Zuhoerer ausfuehren. Der Zuhoerer wird
 * entfernt, sobald der Aufruf endet — ausser bei `nachlaufendBei` (Android-
 * Dialog offen): dann erst beim naechsten Endereignis. Ein Endereignis
 * (`fertig`/`fehler`), das VOR der Antwort kam, wird gemerkt und beendet den
 * Zuhoerer sofort (Codex R3). Ist `signal` beim Anmelden schon abgebrochen,
 * wird der Aufruf gar nicht erst gestartet (Codex R4).
 */
async function mitZuhoerer<T extends string>(
    melde: (s: ApkUpdateStatus) => void,
    aufruf: () => Promise<{ phase?: unknown } | undefined>,
    erlaubt: readonly T[],
    nachlaufendBei: T | null,
    signal?: AbortSignal,
): Promise<T | 'fehler' | 'abgebrochen'> {
    let griff: PluginListenerHandle | null = null;
    let entfernt = false;
    const entferne = (): void => {
        if (entfernt) return;
        entfernt = true;
        const g = griff;
        griff = null;
        if (g) void g.remove().catch(() => undefined);
    };
    try {
        let endeGesehen = false;
        let nachDemAufruf = false;
        griff = await apkUpdatePlugin().addListener('status', (roh) => {
            const s = normalisiereApkStatus(roh);
            if (!s) return;
            melde(s);
            if (s.phase === 'fertig' || s.phase === 'fehler') {
                endeGesehen = true;
                if (nachDemAufruf) {
                    if (nachlaufenderZuhoerer === entferne) nachlaufenderZuhoerer = null;
                    entferne();
                }
            }
        });

        // Direkt vor dem nativen Aufruf: abgebrochen → nicht starten.
        if (signal?.aborted) {
            entferne();
            return 'abgebrochen';
        }
        const antwort = await aufruf();
        const phase = antwort?.phase;
        const ergebnis = typeof phase === 'string' && (erlaubt as readonly string[]).includes(phase) ? (phase as T) : null;
        if (!ergebnis) {
            entferne();
            melde({ phase: 'fehler', prozent: null, meldung: 'Unerwartete Antwort der App beim Update.' });
            return 'fehler';
        }
        if (ergebnis === nachlaufendBei && !endeGesehen) {
            nachDemAufruf = true;
            nachlaufenderZuhoerer = entferne;
        } else {
            entferne();
        }
        return ergebnis;
    } catch (e) {
        entferne();
        melde({ phase: 'fehler', prozent: null, meldung: apkFehlerText(e) });
        return 'fehler';
    }
}

/**
 * Phase 1: Update fuer `release` laden und pruefen. Wirft nie.
 *
 * Die Download-Adresse kommt frisch von `apkDownloadLink` (signiert, 600 s)
 * und muss `istErlaubteApkUrl` bestehen, die Pruefsumme aus `release.sha256`.
 * `onStatus` bekommt jede Stufe, auch die Fehlermeldung.
 *
 * `signal` (Codex R4): wird waehrend der Link-Erzeugung oder vor dem nativen
 * `installieren` abgebrochen, startet der Download nicht — Ergebnis
 * `abgebrochen`, ohne weitere Meldung.
 */
export async function bereiteApkUpdateVor(
    release: AppRelease,
    onStatus: (status: ApkUpdateStatus) => void,
    opts: { signal?: AbortSignal } = {},
): Promise<ApkVorbereitung> {
    const { signal } = opts;
    nachlaufendenEntfernen();
    const melde = melder(onStatus);
    const fehler = (meldung: string): 'fehler' => {
        melde({ phase: 'fehler', prozent: null, meldung });
        return 'fehler';
    };

    if (signal?.aborted) return 'abgebrochen';
    if (!apkUpdateVerfuegbar()) return fehler('Diese App-Version kann sich nicht selbst aktualisieren.');
    const sha256 = normalisiereSha256(release?.sha256);
    if (!sha256) return fehler('Die Angaben zur neuen Version sind unvollständig (Prüfsumme fehlt).');

    melde({ phase: 'laden', prozent: null, meldung: null });
    let url: string | null;
    try {
        url = await apkDownloadLink(release);
    } catch {
        url = null;
    }
    // Abgebrochen, waehrend der Link erzeugt wurde: nicht mehr starten.
    if (signal?.aborted) return 'abgebrochen';
    if (!url) return fehler('Der Download-Link ließ sich nicht erzeugen. Bitte Netz und Anmeldung prüfen und erneut versuchen.');
    if (!istErlaubteApkUrl(url)) return fehler('Updates werden nur aus dem App-Speicher über https geladen.');
    const link = url;

    return mitZuhoerer(melde, () => apkUpdatePlugin().installieren({ url: link, sha256 }), ['bereit', 'erlaubnis_noetig'] as const, null, signal);
}

/**
 * Phase 2: die vorbereitete, gepruefte Datei installieren. Wirft nie. Erst
 * aufrufen, NACHDEM die Eingaben gesichert sind — die App wird dabei beendet.
 */
export async function installiereApkUpdate(onStatus: (status: ApkUpdateStatus) => void): Promise<ApkUpdateErgebnis> {
    nachlaufendenEntfernen();
    const melde = melder(onStatus);
    if (!apkUpdateVerfuegbar()) {
        melde({ phase: 'fehler', prozent: null, meldung: 'Diese App-Version kann sich nicht selbst aktualisieren.' });
        return 'fehler';
    }
    const ergebnis = await mitZuhoerer(melde, () => apkUpdatePlugin().freigeben(), ['bestaetigen', 'fertig', 'erlaubnis_noetig'] as const, 'bestaetigen');
    // Ohne Signal kann nichts abgebrochen sein; der Zweig haelt nur den Typ ehrlich.
    return ergebnis === 'abgebrochen' ? 'fehler' : ergebnis;
}

/** Laufenden Download abbrechen. Wirft nie. */
export async function brichApkUpdateAb(): Promise<void> {
    if (!apkUpdateVerfuegbar()) return;
    try {
        await apkUpdatePlugin().abbrechen();
    } catch {
        // Aeltere Huelle ohne Methode oder Bruecke weg — der Lauf endet dann von selbst.
    }
}

/** Ist „Apps aus dieser Quelle zulassen" erteilt? Unbekannt/Fehler → false. Wirft nie. */
export async function apkInstallErlaubt(): Promise<boolean> {
    if (!apkUpdateVerfuegbar()) return false;
    try {
        const antwort = await apkUpdatePlugin().erlaubnis();
        return antwort?.erlaubt === true;
    } catch {
        return false;
    }
}

/** Nur fuer Tests: Registrierung und nachlaufenden Zuhoerer vergessen. */
export const __setzeApkUpdateZurueck = (): void => {
    plugin = null;
    nachlaufenderZuhoerer = null;
};
